import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Grid,
  Group,
  LoadingOverlay,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { CREATE_STOCK } from "apollo/mutuations";
import { GET_MY_PRODUCTS, GET_MY_WARE_HOUSES } from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";

const StockAddModal = ({ setOpened, fetchData, size, totalPages }) => {
  // State variables
  const [productSkusDropDownData, setProductSkusDropDownData] = useState([]);
  const [tempProducts, setTempProducts] = useState([]);
  const [dropDownData, setDropDownData] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState();
  const [warehousesDropDownData, setWarehousesDropDownData] = useState([]);
  const roles = JSON.parse(localStorage.getItem("roles")) || [];
  const hasOnlySupplierPermission = roles.some(
    (permission) => permission === "supplier",
  );

  // Form state
  const form = useForm({
    initialValues: {
      quantity: null,
      product_sku: "",
      product: null,
      warehouse: "",
      minimum_stock_level: null,
    },
    validate: {
      quantity: (value) => {
        if (value === null || value === "" || value === undefined)
          return "Quantity is required";
        if (isNaN(Number(value))) return "Must be a number";
        if (Number(value) < 0) return "Quantity can't be negative";
        if (Number(value) === 0) return "Quantity can't be zero";
        return null;
      },
      minimum_stock_level: (value) => {
        if (value === null || value === "" || value === undefined)
          return "Minimum stock level is required";
        if (isNaN(Number(value))) return "Must be a number";
        if (Number(value) < 0) return "Minimum stock level can't be negative";
        if (Number(value) === 0) return "Minimum stock level can't be zero";
        return null;
      },
    },
  });

  const [addStock, { loading: stockLoading }] = useMutation(CREATE_STOCK);

  const {
    data,
    loading: productskuLoading,
    refetch,
  } = useQuery(GET_MY_PRODUCTS, {
    variables: {
      first: 10000,
      page: 1,
    },
    onCompleted(data) {
      const productArr = [];
      data.myProducts.data.forEach((item) => {
        productArr.push({ label: item.name, value: item.id });
      });
      setDropDownData(productArr);
      setTempProducts(data.myProducts.data);
    },
  });

  const { loading: warehouseLoading, refetch: warehouseRefetch } = useQuery(
    GET_MY_WARE_HOUSES,
    {
      variables: {
        first: 100000,
        page: 1,
      },
      onCompleted(data) {
        const arr = data.myWarehouses.data
          .filter((item) => item.status === "ACTIVE")
          .map((item) => ({
            label: item.name,
            value: item.id,
          }));
        setWarehousesDropDownData(arr);
      },
      onError(err) {
        showNotification({
          color: "red",
          title: "Error",
          message: `${err}`,
        });
      },
    },
  );

  useEffect(() => {
    if (tempProducts.length) {
      let product = tempProducts.find(
        (item) => item.id === selectedProduct?.id,
      );
      setProductSkusDropDownData(product?.skus || []);
    }
  }, [selectedProduct, tempProducts]);

  useEffect(() => {
    refetch();
    warehouseRefetch();
  }, []);

  const { height } = useViewportSize();

  const submit = () => {
    let variables = {
      quantity: parseInt(form.values.quantity),
      product_sku_id: form.values.product_sku,
      minimum_stock_level: parseInt(form.values.minimum_stock_level),
    };
    if (hasOnlySupplierPermission) {
      variables.warehouse_id = form.values.warehouse;
    }
    addStock({
      variables,
      onCompleted(data) {
        showNotification({
          color: "green",
          title: "Success",
          message: "Stock Created Successfully",
        });
        fetchData(size);
        setOpened(false);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to create stock",
        });
      },
    });
  };

  // Set the selected value to the form state
  const setPrductSkuDropDownValue = (val) => {
    form.setFieldValue("product_sku", val);
  };

  const setWarehouseDropDownValue = (val) => {
    form.setFieldValue("warehouse", val);
  };

  const setProductToSelect = (val) => {
    form.setFieldValue("product", parseInt(val));
    form.setFieldValue("product_sku", ""); // Reset product_sku when product changes
    let product = tempProducts.find((item) => item.id === val);
    setProductSkusDropDownData(product?.skus || []);
    setSelectedProduct(product);
  };

  const selectedSku = productSkusDropDownData.find(
    (sku) => sku.id === form.values.product_sku,
  );

  return (
    <>
      <LoadingOverlay
        visible={productskuLoading || stockLoading || warehouseLoading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(() => submit())}>
          <Stack>
            <Grid>
              <Grid.Col span={3}>
                <TextInput
                  required
                  type="number"
                  label="Quantity"
                  placeholder="Quantity"
                  {...form.getInputProps("quantity")}
                />

                <Select
                  required
                  searchable
                  data={dropDownData}
                  disabled={productskuLoading}
                  value={form.getInputProps("product")?.value?.toString()}
                  onChange={setProductToSelect}
                  label="Select Product"
                  placeholder="Pick a product"
                  styles={(theme) => ({
                    item: {
                      "&[data-selected]": {
                        "&, &:hover": {
                          backgroundColor:
                            theme.colorScheme === "dark"
                              ? theme.colors.blue[6]
                              : theme.colors.blue[3],
                          color:
                            theme.colorScheme === "dark"
                              ? theme.white
                              : theme.colors.black,
                        },
                      },
                      "&[data-hovered]": {
                        backgroundColor:
                          theme.colorScheme === "dark"
                            ? theme.colors.blue[9]
                            : theme.colors.blue[3],
                        color:
                          theme.colorScheme === "dark"
                            ? theme.white
                            : theme.colors.blue[9],
                      },
                    },
                  })}
                />
                <Select
                  required
                  data={productSkusDropDownData?.map((sub) => ({
                    label: sub.sku,
                    value: sub.id,
                  }))}
                  value={form.getInputProps("product_sku")?.value?.toString()}
                  onChange={setPrductSkuDropDownValue}
                  label="Product SKU"
                  placeholder="Pick a product SKU"
                  searchable
                  disabled={productSkusDropDownData?.length === 0}
                />
              </Grid.Col>

              <Grid.Col span={3}>
                <TextInput
                  required
                  type="number"
                  label="Minimum Stock Level"
                  placeholder="Minimum Stock Level"
                  {...form.getInputProps("minimum_stock_level")}
                />
                {hasOnlySupplierPermission && (
                  <Select
                    required
                    data={warehousesDropDownData}
                    value={form.getInputProps("warehouse")?.value.toString()}
                    onChange={setWarehouseDropDownValue}
                    label="Warehouse"
                    placeholder="Pick a warehouse"
                    searchable
                  />
                )}
                {selectedSku && (
                  <Group
                    spacing="md"
                    wrap="wrap"
                    style={{ marginTop: "1.5rem" }}
                  >
                    {selectedSku.variants?.length > 0 && (
                      <>
                        {selectedSku.variants.map((variant) => (
                          <Text key={variant.id} size="sm">
                            {variant.attribute.name}: {variant.value}
                          </Text>
                        ))}
                      </>
                    )}
                  </Group>
                )}
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <Button
                  style={{
                    width: "25%",
                    marginTop: "15px",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  type="submit"
                  fullWidth
                >
                  Submit
                </Button>
              </Grid.Col>
            </Grid>
          </Stack>
        </form>
      </ScrollArea>
    </>
  );
};

export default StockAddModal;
